import { css } from 'lit';

export const buttonStyles = css`
  @keyframes spin360 {
    100% {
      transform: rotate(1turn);
    }
  }

  :host {
    box-sizing: border-box;
    display: inline-block;
    position: relative;
  }

  .processing-icon {
    position: absolute;
    inset: 0;
    margin: auto;
    animation: spin360 1s linear infinite;
  }

  button {
    align-items: center;
    background: var(--token-component-button-primary-default-background, light-dark(#0174BC, #5bb3ea));
    border: 1px solid var(--token-component-button-primary-default-background, light-dark(#0174bc, #5bb3ea));
    box-sizing: border-box;
    color: var(--token-component-button-primary-default-text, light-dark(#ffffff, #343438));
    cursor: pointer;
    display: inline-flex;
    font-family: var(--token-global-font-font-family-brand, "Nunito Sans");
    font-size: var(--token-global-font-font-size-xs, 0.875rem); /* 14px */
    font-style: normal;
    font-weight: var(--token-global-font-font-weight-semibold, 600);
    gap: var(--token-global-spacing-s4, 4px);
    height: 34px;
    justify-content: center;
    line-height: var(--token-global-font-line-height-xxxs, 1rem); /* 16px */
    padding: var(--token-global-spacing-s8, 8px) var(--token-global-spacing-s12, 12px);
    text-align: center;
    width: 100%;
  }

  button:disabled {
    cursor: not-allowed;
  }

  button.large {
    height: 34px;
    padding: var(--token-global-spacing-s8, 8px) var(--token-global-spacing-s12, 12px);
  }

  button.small {
    height: 26px;
    font-size: 12px;
    padding: var(--token-global-spacing-s4, 4px) var(--token-global-spacing-s8, 8px);
  }

  /* PRIMARY */
  button.primary {
    background: var(--token-component-button-primary-default-background, light-dark(#0174bc, #5bb3ea));
    border: 1px solid var(--token-component-button-primary-default-background, light-dark(#0174bc, #5bb3ea));
    color: var(--token-component-button-primary-default-text, light-dark(#ffffff, #343438));
  }
  button.primary:hover, button.primary:focus {
    background: var(--token-component-button-primary-hover-background, light-dark(#009CDE, #96cff0));
    border: 1px solid var(--token-component-button-primary-hover-background, light-dark(#009cde, #96cff0));
    color: var(--token-component-button-primary-hover-text, light-dark(#ffffff, #343438));
  }
  button.primary:active {
    background: var(--token-component-button-primary-pressed-background, light-dark(#0174bc, #5bb3ea));
    border: 1px solid var(--token-component-button-primary-pressed-background, light-dark(#0174bc, #5bb3ea));
    color: var(--token-component-button-primary-pressed-text, light-dark(#ffffff, #343438));
  }
  button.primary:disabled {
    background: var(--token-component-button-primary-disabled-background, light-dark(#e5e5ea, #515157));
    border: 1px solid var(--token-component-button-primary-disabled-background, light-dark(#e5e5ea, #515157));
    color: var(--token-component-button-primary-disabled-text, light-dark(#a3a3ab, #83838c));
  }
  button.primary.processing {
    border: 1px solid var(--token-component-button-primary-loading-background, light-dark(#0174bc, #5bb3ea));
    background: var(--token-component-button-primary-loading-background, light-dark(#0174bc, #5bb3ea));
    color: transparent;
  }

  /* SECONDARY */
  button.secondary {
    background: var(--token-component-button-secondary-default-background, light-dark(#ffffff, #3d3d42));
    border: 1px solid var(--token-component-button-secondary-default-border, light-dark(#0174bc, #5bb3ea));
    color: var(--token-component-button-secondary-default-text, light-dark(#0174bc, #7dcdff));
  }
  button.secondary:hover, button.secondary:focus {
    background: var(--token-component-button-secondary-hover-background, light-dark(#edf8ff, #515157));
    border: 1px solid var(--token-component-button-secondary-hover-border, light-dark(#009cde, #96cff0));
    color: var(--token-component-button-secondary-hover-text, light-dark(#0174bc, #7dcdff));
  }
  button.secondary:active {
    background: var(--token-component-button-secondary-pressed-background, light-dark(#ffffff, #3d3d42));
    border: 1px solid var(--token-component-button-secondary-pressed-border, light-dark(#0174bc, #5bb3ea));
    color: var(--token-component-button-secondary-pressed-text, light-dark(#0174bc, #7dcdff));
  }
  button.secondary:disabled {
    background: var(--token-component-button-secondary-disabled-background, light-dark(#ffffff, #3d3d42));
    border: 1px solid var(--token-component-button-secondary-disabled-border, light-dark(#c2c2ca, #515157));
    color: var(--token-component-button-secondary-disabled-text, light-dark(#a3a3ab, #83838c));
  }
  button.secondary.processing {
    background: var(--token-component-button-secondary-loading-background, light-dark(#ffffff, #3d3d42));
    border: 1px solid var(--token-component-button-secondary-loading-border, light-dark(#0174bc, #5bb3ea));
    color: transparent;
  }

  /* LINK */
  button.link {
    background: transparent;
    border: none;
    padding: 0;
    height: auto;
    color: var(--token-component-button-link-default-text, light-dark(#0174bc, #7dcdff));
  }
  button.link:hover, button.link:focus {
    color: var(--token-component-button-link-hover-text, light-dark(#009cde, #96cff0));
  }
  button.link:active {
    color: var(--token-component-button-link-pressed-text, light-dark(#0174bc, #7dcdff));
  }
  button.link:disabled {
    color: var(--token-component-button-link-disabled-text, light-dark(#a3a3ab, #83838c));
  }
  button.link.processing {
    color: transparent;
  }

  /* LAUNCH */
  button.launch {
    background: var(--token-component-button-launch-default-background, light-dark(#232326, #232326));
    border: 1px solid var(--token-component-button-launch-default-background, light-dark(#232326, #232326));
    color: var(--token-component-button-launch-default-text, light-dark(#ffffff, #ffffff));
  }
  button.launch:hover, button.launch:focus {
    background: var(--token-component-button-launch-hover-background, light-dark(#343438, #343438));
    border: 1px solid var(--token-component-button-launch-hover-background, light-dark(#343438, #343438));
    color: var(--token-component-button-launch-hover-text, light-dark(#ffffff, #ffffff));
  }
  button.launch:active {
    background: var(--token-component-button-launch-pressed-background, light-dark(#232326, #232326));
    border: 1px solid var(--token-component-button-launch-pressed-background, light-dark(#232326, #232326));
    color: var(--token-component-button-launch-pressed-text, light-dark(#ffffff, #ffffff));
  }
  button.launch:disabled {
    background: var(--token-component-button-launch-disabled-background, light-dark(#e5e5ea, #e5e5ea));
    border: 1px solid var(--token-component-button-launch-disabled-background, light-dark(#e5e5ea, #e5e5ea));
    color: var(--token-component-button-launch-disabled-text, light-dark(#a3a3ab, #a3a3ab));
  }
  button.launch.processing {
    background: var(--token-component-button-launch-loading-background, light-dark(#232326, #232326));
    border: 1px solid var(--token-component-button-launch-loading-background, light-dark(#232326, #232326));
    color: transparent;
  }

  /* DANGER */
  button.danger {
    background: var(--token-component-button-danger-default-background, light-dark(#b82329, #eb9091));
    border: 1px solid var(--token-component-button-danger-default-background, light-dark(#b82329, #eb9091));
    color: var(--token-component-button-danger-default-text, light-dark(#ffffff, #343438));
  }
  button.danger:hover, button.danger:focus {
    background: var(--token-component-button-danger-hover-background, light-dark(#d63c42, #f3aeaf));
    border: 1px solid var(--token-component-button-danger-hover-background, light-dark(#d63c42, #f3aeaf));
    color: var(--token-component-button-danger-hover-text, light-dark(#ffffff, #343438));
  }
  button.danger:active {
    background: var(--token-component-button-danger-pressed-background, light-dark(#b82329, #eb9091));
    border: 1px solid var(--token-component-button-danger-pressed-background, light-dark(#b82329, #eb9091));
    color: var(--token-component-button-danger-pressed-text, light-dark(#ffffff, #343438));
  }
  button.danger:disabled {
    background: var(--token-component-button-danger-disabled-background, light-dark(#f9c6c6, #515157));
    border: 1px solid var(--token-component-button-danger-disabled-background, light-dark(#f9c6c6, #515157));
    color: var(--token-component-button-danger-disabled-text, light-dark(#a3a3ab, #83838c));
  }
  button.danger.processing {
    background: var(--token-component-button-danger-loading-background, light-dark(#b82329, #eb9091));
    border: 1px solid var(--token-component-button-danger-loading-background, light-dark(#b82329, #eb9091));
    color: transparent;
  }

  /* ICON */
  button.icon {
    background: none;
    border: none;
    padding: 0;
    color: var(--token-alias-content-text-primary-default, light-dark(#343438, #ffffff));
  }
  button.icon:hover, button.icon:focus {
    color: var(--token-alias-content-text-primary-hover, light-dark(#009cde, #96cff0));
  }
  button.icon:active {
    color: var(--token-alias-content-text-primary-active, light-dark(#0174bc, #7dcdff));
  }
  button.icon.disabled {
    color: var(--token-component-button-link-disabled-text, light-dark(#a3a3ab, #83838c));
  }
  button.icon.processing {
    color: transparent;
  }

  .processing-icon {
    cursor: not-allowed;
  }

  /* SLOTTED ICON COLOR SYNC FOR ALL VARIANTS */
  :host ::slotted(cds-icon) {
    color: inherit;
  }
  :host([processing]) ::slotted(cds-icon) {
    cursor: not-allowed;
  }
  :host([disabled]) ::slotted(cds-icon) {
    color: var(--token-alias-content-icon-primary-disabled, light-dark(#a3a3ab, #83838c)) !important;
    cursor: not-allowed;
    pointer-events: none;
  }
`;